import {ClientAnalytics} from '@shopify/hydrogen';
import {
  ReactNode,
  Dispatch,
  SetStateAction,
  useContext,
  createContext,
  useCallback,
  useState,
} from 'react';
import {saveMyProductRating} from '../backendApi';
import RateWhiskyModal from '../components/modals/RateWhiskyModal.client';
import SwalComponent from '../components/modals/SwalComponent.client';
import {TwlAnalyticsEvent} from '../constants';

import {useModalToggle} from '../hooks';
import {MemberProductScoreType, MemberProductType} from '../types';

export interface RatingModalDataType {
  productId: string;
  productImage: string;
  productTitle: string;
  rating: MemberProductScoreType;
  onSuccess?: (memberProduct: MemberProductType) => void;
  skipSuccessModal?: boolean;
}

type RatingModalContextType = {
  openRatingModal: (data?: RatingModalDataType) => void;
  toggleRatingModal: () => void;
  closeRatingModal: () => void;
  ratingModalData: RatingModalDataType;
  setRatingModalData: Dispatch<SetStateAction<RatingModalDataType>>;
  handleRatingChange: (
    rating: MemberProductScoreType,
  ) => Promise<MemberProductType>;
  updatedMemberProduct: MemberProductType;
};

const RatingModalContext = createContext<RatingModalContextType>(null);

interface RatingModalProviderProps {
  children: ReactNode;
}

function RatingModalProvider({children}: RatingModalProviderProps) {
  const {
    isOpen,
    openModal: openRatingModal,
    closeModal: closeRatingModal,
    toggleModal: toggleRatingModal,
    modalData: ratingModalData,
    setModalData: setRatingModalData,
  } = useModalToggle<RatingModalDataType>('rating', {
    productId: null,
    productImage: null,
    productTitle: null,
    rating: null,
    onSuccess: null,
  });

  const [updatedMemberProduct, setUpdatedMemberProduct] =
    useState<MemberProductType>(null);

  const handleRatingChange = useCallback(
    async (rating: MemberProductScoreType) => {
      try {
        const responseData = await saveMyProductRating({
          product: ratingModalData.productId,
          rating,
        });
        ClientAnalytics.publish(TwlAnalyticsEvent.RATE_PRODUCT, true, {
          ...ClientAnalytics.getPageAnalyticsData(),
          productId: responseData.product?.uuid,
          oldRating: ratingModalData.rating
            ? ratingModalData.rating
            : undefined,
          newRating: responseData.rating,
        });
        closeRatingModal();
        if (!ratingModalData?.skipSuccessModal) {
          SwalComponent.fire({
            title: 'Thanks for your rating!',
            icon: 'success',
          });
        }
        ratingModalData.onSuccess?.(responseData);
        setRatingModalData((prevState) => ({...prevState, rating}));
        setUpdatedMemberProduct(responseData);
        return responseData;
      } catch (e) {
        SwalComponent.fire({
          title: 'Something went wrong!',
          icon: 'error',
        });
        return null;
      }
    },
    [ratingModalData, closeRatingModal, setRatingModalData],
  );

  return (
    <RatingModalContext.Provider
      value={{
        openRatingModal,
        closeRatingModal,
        toggleRatingModal,
        ratingModalData,
        setRatingModalData,
        handleRatingChange,
        updatedMemberProduct,
      }}
    >
      {children}
      <RateWhiskyModal isOpen={isOpen} onRequestClose={closeRatingModal} />
    </RatingModalContext.Provider>
  );
}

function useRatingModal() {
  const context = useContext(RatingModalContext);
  if (context === undefined) {
    throw new Error('useRatingModal cannot work without ModalUIContext');
  }

  return context;
}

export {RatingModalProvider, useRatingModal};
